import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRBAC } from '../hooks/useRBAC';
import { usersService, User } from '../services/usersService';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { ModernStaffCreateDialog } from './ModernStaffCreateDialog';
import { ModernStaffProfileEditDialog } from './ModernStaffProfileEditDialog';
import { staffProfileService } from '../services/staffProfileService';
import { nekoStaff } from 'public-contracts';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

// Lucide React icons
import {
  Plus,
  Edit,
  Search,
  Users,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Shield,
  UserX,
  UserCheck,
  Trash2,
  UserCircle,
  Briefcase,
} from 'lucide-react';

import { filterUsersByRole, getUserHighestRoleLevel, filterRelevantRoles } from '../utils/userFiltering';

const ModernStaffManagement: React.FC = () => {
  const { translate } = useTranslation();
  const { hasPermission } = useRBAC();
  const { notify } = useNotification();
  const { user: currentUser } = useUserStore();

  // Permission checks
  const canCreate = hasPermission('staff_profiles', 'create');
  const canEdit = hasPermission('staff_profiles', 'edit');
  const canDelete = hasPermission('staff_profiles', 'delete');
  
  // Ensure orgAdmin and storeManager can always edit staff profiles
  // This is a fallback in case permission check fails
  const isOrgAdmin = currentUser?.roles?.includes('orgAdmin');
  const isStoreManagerRole = currentUser?.roles?.includes('storeManager');
  const canEditProfiles = canEdit || isOrgAdmin || isStoreManagerRole;

  // Store managers can deactivate staff members (users with lower role levels)
  const viewerRoleLevel = getUserHighestRoleLevel(currentUser?.roles || []);
  const isStoreManager = viewerRoleLevel === 3;
  const canDeactivateStaff = canEdit || isStoreManager || isOrgAdmin;

  // Pagination state
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
  });

  // Filter users based on role hierarchy
  const users = useMemo(() => {
    return filterUsersByRole(allUsers, currentUser?.roles || []);
  }, [allUsers, currentUser?.roles]);

  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedOnceRef = useRef(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [toggleActiveDialogOpen, setToggleActiveDialogOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState<User | null>(null);
  const [profileEditDialogOpen, setProfileEditDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<nekoStaff.StaffProfile | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      if (searchTerm !== debouncedSearchTerm) {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, debouncedSearchTerm]);

  // Load users with pagination
  const loadUsers = useCallback(
    async (retryCount = 0) => {
      const maxRetries = 3;
      const currentPage = pagination.page;
      const currentPerPage = pagination.perPage;
      const currentSearch = debouncedSearchTerm;

      try {
        if (
          !hasLoadedOnceRef.current &&
          currentPage === 1 &&
          retryCount === 0
        ) {
          setInitialLoading(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const response = await usersService.getUsers({
          page: currentPage,
          size: currentPerPage,
          q: currentSearch || undefined,
        });

        const usersArray = Array.isArray(response) ? response : [];
        const total = (response as any).__total ?? 0;

        setAllUsers(usersArray);
        // Note: total might be higher than filtered count, but we'll use it as-is
        // The actual filtered count will be users.length
        setPagination((prev) => ({ ...prev, total }));
        hasLoadedOnceRef.current = true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load users';
        console.error('Failed to load users:', err);

        if (
          retryCount < maxRetries &&
          (errorMessage.includes('Failed to fetch') ||
            errorMessage.includes('ERR_CONNECTION_REFUSED') ||
            errorMessage.includes('NetworkError'))
        ) {
          await new Promise((resolve) =>
            setTimeout(resolve, (retryCount + 1) * 1000)
          );
          return loadUsers(retryCount + 1);
        }

        setError(errorMessage);
        setAllUsers([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        notify(errorMessage, { type: 'error' });
      } finally {
        setInitialLoading(false);
        setLoading(false);
      }
    },
    [pagination.page, pagination.perPage, debouncedSearchTerm, notify]
  );

  // Load users when pagination or search changes
  useEffect(() => {
    loadUsers();
  }, [pagination.page, pagination.perPage, debouncedSearchTerm, loadUsers]);

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(pagination.total / pagination.perPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handlePerPageChange = (newPerPage: string) => {
    const perPage = parseInt(newPerPage, 10);
    // Reset to first page and maintain current search filter
    setPagination((prev) => ({
      ...prev,
      perPage,
      page: 1, // Reset to first page when changing items per page
    }));
    // The useEffect will trigger loadUsers with the new perPage and current debouncedSearchTerm
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
  };

  const handleEditProfile = async (user: User) => {
    setLoadingProfile(true);
    setEditingUserId(user.id); // Store userId for creating profile if needed
    try {
      // Try to fetch the staff profile for this user
      console.log(`🔍 Loading staff profile for user ${user.id} (${user.email})`);
      
      // First check for all profiles to detect duplicates
      const allProfiles = await staffProfileService.getAllStaffProfilesByUserId(user.id);
      
      if (allProfiles.length > 1) {
        console.error(`⚠️ Found ${allProfiles.length} duplicate profiles for ${user.email}`);
        // Still proceed but warn the user
        notify(
          translate(
            'pos.staff.profile.duplicate_profiles_detected',
            `Warning: Found ${allProfiles.length} profiles. Please contact administrator to merge them.`
          ),
          { type: 'warning' }
        );
      }
      
      // Get the profile (getStaffProfileByUserId will return the best one if duplicates exist)
      const profile = await staffProfileService.getStaffProfileByUserId(user.id);
      
      console.log('✅ Profile loaded:', {
        profileId: profile.id,
        userId: profile.userId,
        hasMetadata: !!(profile as any).metadata,
        metadataKeys: (profile as any).metadata ? Object.keys((profile as any).metadata) : [],
        employmentType: (profile as any).metadata?.employmentType || (profile as any).employmentType,
        baseSalary: (profile as any).metadata?.baseSalary || (profile as any).baseSalary,
        guaranteedHours: (profile as any).metadata?.guaranteedHoursPerWeek || (profile as any).guaranteedHoursPerWeek,
        vacationDays: (profile as any).metadata?.vacationDaysPerYear || (profile as any).vacationDaysPerYear,
        fullProfile: JSON.stringify(profile, null, 2),
      });
      
      // Ensure profile has an id (critical for form to recognize it as existing)
      if (!profile.id) {
        console.error('❌ Profile missing id field!', profile);
        throw new Error('Profile loaded but missing id field');
      }
      
      setEditingProfile(profile);
      setProfileEditDialogOpen(true);
    } catch (err) {
      console.error('❌ Error loading profile:', err);
      // If profile doesn't exist, open dialog with null profile but userId
      // The dialog will handle creating the profile
      if (err instanceof Error && err.message.includes('not found')) {
        console.log('   Profile not found, will create new one');
        setEditingProfile(null);
        setProfileEditDialogOpen(true);
      } else {
        notify(
          translate(
            'pos.staff.profile.profile_load_failed',
            'Failed to load staff profile'
          ),
          { type: 'error' }
        );
        setEditingUserId(null);
      }
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    // Prevent deleting yourself
    if (user.id === currentUser?.id) {
      notify('You cannot delete your own account', { type: 'error' });
      return;
    }

    // Check permissions based on role hierarchy
    const viewerRoleLevel = getUserHighestRoleLevel(currentUser?.roles || []);
    const userRoleLevel = getUserHighestRoleLevel(user.roles || []);

    // Users can only delete users with lower role levels
    if (viewerRoleLevel <= userRoleLevel) {
      notify('You do not have permission to delete this user', {
        type: 'error',
      });
      return;
    }

    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setDeleteDialogOpen(false);

    try {
      await usersService.deleteUser(userToDelete.id);
      notify('User deleted successfully', { type: 'success' });
      loadUsers();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete user';
      notify(errorMessage, { type: 'error' });
    } finally {
      setUserToDelete(null);
    }
  };

  const handleToggleActiveClick = (user: User) => {
    // Prevent deactivating yourself
    if (user.id === currentUser?.id) {
      notify('You cannot deactivate your own account', { type: 'error' });
      return;
    }

    // Check permissions based on role hierarchy
    const viewerRoleLevel = getUserHighestRoleLevel(currentUser?.roles || []);
    const userRoleLevel = getUserHighestRoleLevel(user.roles || []);

    // Store managers can only deactivate staff (level < 3), not other store managers or admins
    if (viewerRoleLevel === 3 && userRoleLevel >= 3) {
      notify('You do not have permission to deactivate this user', {
        type: 'error',
      });
      return;
    }

    // Users with higher or equal role level cannot be deactivated by lower level users
    if (viewerRoleLevel <= userRoleLevel) {
      notify('You do not have permission to deactivate this user', {
        type: 'error',
      });
      return;
    }

    setUserToToggle(user);
    setToggleActiveDialogOpen(true);
  };

  const handleConfirmToggleActive = async () => {
    if (!userToToggle) return;

    const newStatus = !userToToggle.isActive;
    const action = newStatus ? 'activate' : 'deactivate';

    setToggleActiveDialogOpen(false);

    try {
      await usersService.updateUser(userToToggle.id, { isActive: newStatus });
      notify(`User ${action}d successfully`, { type: 'success' });
      loadUsers();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : `Failed to ${action} user`;
      notify(errorMessage, { type: 'error' });
    } finally {
      setUserToToggle(null);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'sysAdmin':
      case 'admin':
        return 'destructive';
      case 'storeManager':
        return 'default';
      case 'staff':
        return 'secondary';
      case 'cashier':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (initialLoading) {
    return (
      <div className="space-y-6 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">
              {translate('pos.common.action.loading', 'Loading...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !hasLoadedOnceRef.current) {
    return (
      <div className="space-y-6 py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive">
              <p className="text-lg font-medium">
                {translate('pos.common.notification.error', 'Error')}
              </p>
              <p className="text-sm text-muted-foreground mt-2">{error}</p>
              <Button
                onClick={() => loadUsers()}
                className="mt-4"
                variant="outline"
              >
                {translate('pos.common.action.refresh', 'Retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalPages = Math.ceil(pagination.total / pagination.perPage);
  const filteredTotal = users.length;
  const startIndex = (pagination.page - 1) * pagination.perPage + 1;
  const endIndex = Math.min(
    pagination.page * pagination.perPage,
    pagination.total
  );

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.menu.staff', 'Staff Management')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate(
              'pos.staff.management.subtitle',
              'Manage staff members and their roles'
            )}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {translate('pos.common.action.create', 'Create')}
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>{translate('pos.users.name', 'Users')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={translate('pos.common.action.search', 'Search users...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading && !initialLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {!loading && (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {translate('pos.users.fields.email', 'Email')}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.users.fields.firstName',
                          'First Name'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.users.fields.lastName',
                          'Last Name'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate('pos.users.fields.roles', 'Roles')}
                      </TableHead>
                      <TableHead>
                        {translate('pos.users.fields.status', 'Status')}
                      </TableHead>
                      <TableHead className="text-right">
                        {translate('pos.common.action.actions', 'Actions')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center py-8 text-muted-foreground"
                        >
                          {translate('pos.common.message.empty', 'No users found')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => {
                        const isCurrentUser = user.id === currentUser?.id;
                        return (
                          <TableRow key={user.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <span data-sensitive-field="userEmail">
                                  {user.email}
                                </span>
                                {isCurrentUser && (
                                  <Badge
                                    variant="secondary"
                                    className="flex items-center gap-1 text-xs"
                                    title={translate(
                                      'pos.staff.current_user',
                                      'This is your account'
                                    )}
                                  >
                                    <UserCircle className="h-3 w-3" />
                                    {translate('pos.staff.you', 'You')}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span data-sensitive-field="firstName">
                                {user.firstName || '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span data-sensitive-field="lastName">
                                {user.lastName || '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {(() => {
                                  const relevantRoles = filterRelevantRoles(
                                    user.roles
                                  );
                                  return relevantRoles.length > 0 ? (
                                    relevantRoles.map((role) => (
                                      <Badge
                                        key={role}
                                        variant={getRoleBadgeColor(role)}
                                        className="flex items-center gap-1"
                                      >
                                        <Shield className="h-3 w-3" />
                                        {role}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-muted-foreground text-sm">
                                      No roles
                                    </span>
                                  );
                                })()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  user.isActive !== false
                                    ? 'default'
                                    : 'secondary'
                                }
                                className="flex items-center gap-1 w-fit"
                              >
                                {user.isActive !== false ? (
                                  <>
                                    <UserCheck className="h-3 w-3" />
                                    Active
                                  </>
                                ) : (
                                  <>
                                    <UserX className="h-3 w-3" />
                                    Inactive
                                  </>
                                )}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                {(() => {
                                  // Check if user can be deactivated/activated
                                  const userRoleLevel = getUserHighestRoleLevel(
                                    user.roles || []
                                  );
                                  const isSelf = user.id === currentUser?.id;
                                  // Store managers can only deactivate staff (level < 3), not other store managers or admins
                                  const canModifyThisUser =
                                    !isSelf &&
                                    (viewerRoleLevel > userRoleLevel ||
                                      (isStoreManager && userRoleLevel < 3));

                                  // Check if user can be deleted (must have lower role level)
                                  const canDeleteThisUser =
                                    !isSelf && viewerRoleLevel > userRoleLevel;

                                  return (
                                    <>
                                      {canDeactivateStaff &&
                                        canModifyThisUser && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                              handleToggleActiveClick(user)
                                            }
                                            title={
                                              user.isActive !== false
                                                ? 'Deactivate user'
                                                : 'Activate user'
                                            }
                                          >
                                            {user.isActive !== false ? (
                                              <UserX className="h-4 w-4" />
                                            ) : (
                                              <UserCheck className="h-4 w-4" />
                                            )}
                                          </Button>
                                        )}
                                      {(canEdit || canEditProfiles) && (
                                        <>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleEdit(user)}
                                            title="Edit user account"
                                          >
                                            <Edit className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleEditProfile(user)}
                                            disabled={loadingProfile}
                                            title="Edit staff profile"
                                          >
                                            <Briefcase className="h-4 w-4" />
                                          </Button>
                                        </>
                                      )}
                                      {canDelete && canDeleteThisUser && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            handleDeleteClick(user)
                                          }
                                          title="Delete user"
                                          className="text-destructive hover:text-destructive"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.total > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      {translate('pos.orders.modern.pagination.showing', {
                        start: startIndex,
                        end: endIndex,
                        total: pagination.total,
                      })}{' '}
                      {pagination.total === 1
                        ? translate('pos.staff.member', 'staff member')
                        : translate('pos.staff.members', 'staff members')}
                      {filteredTotal < pagination.total && (
                        <span className="ml-1 text-xs">
                          ({filteredTotal}{' '}
                          {translate(
                            'pos.staff.visible_after_filter',
                            'visible after role filter'
                          )})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {translate('pos.staff.items_per_page', 'Items per page')}:
                      </span>
                      <Select
                        value={pagination.perPage.toString()}
                        onValueChange={handlePerPageChange}
                      >
                        <SelectTrigger className="w-20 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.page === 1}
                        title={translate('pos.staff.first_page', 'First page')}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <ChevronLeft className="h-4 w-4 -ml-2" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        title={translate(
                          'pos.staff.previous_page',
                          'Previous page'
                        )}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-2 px-2">
                        <span className="text-sm text-muted-foreground">
                          {translate('common.page', 'Page')}
                        </span>
                        <Input
                          type="number"
                          min={1}
                          max={totalPages}
                          value={pagination.page}
                          onChange={(e) => {
                            const page = parseInt(e.target.value, 10);
                            if (
                              !isNaN(page) &&
                              page >= 1 &&
                              page <= totalPages
                            ) {
                              handlePageChange(page);
                            }
                          }}
                          className="w-16 h-8 text-center"
                        />
                        <span className="text-sm text-muted-foreground">
                          {translate('common.of', 'of')} {totalPages}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= totalPages}
                        title={translate('pos.staff.next_page', 'Next page')}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={pagination.page >= totalPages}
                        title={translate('pos.staff.last_page', 'Last page')}
                      >
                        <ChevronRight className="h-4 w-4" />
                        <ChevronRight className="h-4 w-4 -ml-2" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Staff Dialog */}
      <ModernStaffCreateDialog
        open={createDialogOpen || !!editingUser}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditingUser(null);
        }}
        onSuccess={() => {
          loadUsers();
          setEditingUser(null);
        }}
        user={editingUser}
      />

      {/* Edit Staff Profile Dialog */}
      <ModernStaffProfileEditDialog
        open={profileEditDialogOpen}
        onClose={() => {
          setProfileEditDialogOpen(false);
          setEditingProfile(null);
          setEditingUserId(null);
        }}
        onSuccess={() => {
          setProfileEditDialogOpen(false);
          setEditingProfile(null);
          setEditingUserId(null);
        }}
        profile={editingProfile}
        userId={editingProfile?.userId || editingUserId || undefined}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate('pos.common.message.delete_title', 'Delete User')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {userToDelete
                ? translate(
                    'pos.common.message.delete_content',
                    `Are you sure you want to delete ${userToDelete.firstName} ${userToDelete.lastName}? This action cannot be undone.`
                  )
                : translate(
                    'pos.common.message.delete_content',
                    'Are you sure you want to delete this user? This action cannot be undone.'
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {translate('pos.common.action.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {translate('pos.common.action.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Toggle Active Confirmation Dialog */}
      <AlertDialog
        open={toggleActiveDialogOpen}
        onOpenChange={setToggleActiveDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {userToToggle?.isActive !== false
                ? translate('pos.staff.deactivate_title', 'Deactivate User')
                : translate('pos.staff.activate_title', 'Activate User')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {userToToggle
                ? userToToggle.isActive !== false
                  ? `Are you sure you want to deactivate ${userToToggle.firstName} ${userToToggle.lastName}? They will not be able to log in until reactivated.`
                  : `Are you sure you want to activate ${userToToggle.firstName} ${userToToggle.lastName}? They will be able to log in again.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {translate('pos.common.action.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmToggleActive}>
              {userToToggle?.isActive !== false
                ? translate('pos.staff.deactivate', 'Deactivate')
                : translate('pos.staff.activate', 'Activate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModernStaffManagement;
